import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import StoreLayout from "../components/StoreLayout";
import { api } from "../lib/api";

const STEPS = ["placed", "confirmed", "printed", "shipped", "delivered"];
const STEP_LABELS = {
  placed: "Order Placed",
  confirmed: "Payment Confirmed",
  printed: "Printed",
  shipped: "Shipped",
  delivered: "Delivered",
};

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data));
  }, [id]);

  if (!order) return <StoreLayout><div className="p-20 text-center label-mono">// LOADING ORDER...</div></StoreLayout>;

  const currentIdx = STEPS.indexOf(order.status);
  const timelineMap = (order.timeline || []).reduce((acc, t) => { acc[t.status] = t.at; return acc; }, {});

  return (
    <StoreLayout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <Link to="/orders" className="label-mono text-[#71717A] hover:text-white" data-testid="back-to-orders">← BACK TO ORDERS</Link>
        <div className="mt-4 flex flex-wrap justify-between items-end gap-4">
          <div>
            <p className="label-mono text-[#71717A]">ORDER // {order.id.slice(0,8).toUpperCase()}</p>
            <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight mt-2">Tracking</h1>
          </div>
          <div className="text-right">
            <p className="label-mono text-[#71717A]">PLACED</p>
            <p className="font-mono-cult">{new Date(order.created_at).toLocaleString()}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-10 mt-10">
          {/* Timeline */}
          <div className="lg:col-span-2">
            <div className="border border-[#27272A] p-6 md:p-8" data-testid="order-timeline">
              <p className="label-mono text-[#CFF048] mb-6">// STATUS TIMELINE</p>
              <ol className="relative border-l border-[#27272A] ml-3">
                {STEPS.map((s, i) => {
                  const active = i <= currentIdx;
                  return (
                    <li key={s} className="ml-6 pb-8 last:pb-0">
                      <span className={`absolute -left-[7px] timeline-dot ${active ? "active" : ""}`} />
                      <p className={`font-display font-bold uppercase tracking-tight ${active ? "text-white" : "text-[#71717A]"}`}>
                        {STEP_LABELS[s]}
                      </p>
                      <p className="label-mono text-[#71717A] mt-1">
                        {timelineMap[s] ? new Date(timelineMap[s]).toLocaleString() : "PENDING"}
                      </p>
                    </li>
                  );
                })}
              </ol>
            </div>

            <div className="border border-[#27272A] mt-6 p-6">
              <p className="label-mono text-[#71717A]">// ITEMS</p>
              <div className="mt-4 space-y-3">
                {order.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-3 border-b border-[#18181B] pb-3 last:border-0">
                    <img src={it.image} alt="" className="w-16 h-16 object-cover border border-[#27272A]" />
                    <div className="flex-1 text-sm">
                      <div className="font-display font-bold">{it.name}</div>
                      <div className="label-mono text-[#71717A] mt-1">{it.size}/{it.color} × {it.qty}</div>
                    </div>
                    <div className="font-mono-cult text-[#CFF048]">₹{(it.price * it.qty).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* PAYMENT DETAIL CARD */}
            <div className="border border-[#27272A] p-6" data-testid="payment-detail-card">
              <p className="label-mono text-[#CFF048] mb-4">// PAYMENT DETAIL</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between border-b border-[#18181B] pb-2">
                  <span className="label-mono text-[#71717A]">SUBTOTAL</span>
                  <span className="font-mono-cult">₹{(order.subtotal || order.total).toFixed(2)}</span>
                </div>
                {(order.discount_amount || 0) > 0 && (
                  <div className="flex justify-between border-b border-[#18181B] pb-2" data-testid="payment-discount">
                    <span className="label-mono text-[#CFF048]">BULK LOVE DISCOUNT</span>
                    <span className="font-mono-cult text-[#CFF048]">− ₹{order.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-1">
                  <span className="font-display font-black uppercase">TOTAL PAID</span>
                  <span className="font-mono-cult text-[#CFF048] font-bold text-xl">₹{order.total.toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#27272A] space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="label-mono text-[#71717A]">PAYMENT STATUS</span>
                  <span className={`label-mono font-bold ${order.payment_status === "paid" ? "text-[#CFF048]" : "text-[#A1A1AA]"}`}>
                    {(order.payment_status || "PENDING").toUpperCase()}
                  </span>
                </div>
                {order.payment_ref && (
                  <div className="flex justify-between text-sm" data-testid="payment-ref">
                    <span className="label-mono text-[#71717A]">PAYMENT REF</span>
                    <span className="font-mono-cult text-[#52525B] text-xs truncate max-w-[120px]">…{order.payment_ref.slice(-10)}</span>
                  </div>
                )}
                {order.payment_date && (
                  <div className="flex justify-between text-sm">
                    <span className="label-mono text-[#71717A]">PAID ON</span>
                    <span className="font-mono-cult text-xs">
                      {new Date(order.payment_date).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="label-mono text-[#71717A]">METHOD</span>
                  <span className="label-mono text-[#52525B]">STRIPE // CARD</span>
                </div>
              </div>
            </div>
            {order.status === "shipped" || order.courier_name ? (
              <div className="border border-[#CFF048] p-6 bg-[rgba(207,240,72,0.06)]" data-testid="tracking-info">
                <p className="label-mono text-[#CFF048]">// TRACKING</p>
                <p className="font-display font-bold text-lg mt-2">{order.courier_name || "—"}</p>
                <p className="font-mono-cult text-[#CFF048] mt-1 text-sm">#{order.tracking_number || "—"}</p>
              </div>
            ) : null}
            <div className="border border-[#27272A] p-6">
              <p className="label-mono text-[#71717A]">// SHIPPING TO</p>
              <p className="font-display font-bold mt-2">{order.shipping.full_name}</p>
              <p className="text-sm text-[#A1A1AA] mt-1">{order.shipping.line1}{order.shipping.line2 ? ", " + order.shipping.line2 : ""}</p>
              <p className="text-sm text-[#A1A1AA]">{order.shipping.city}, {order.shipping.state} {order.shipping.pincode}</p>
              <p className="text-sm text-[#A1A1AA]">{order.shipping.country}</p>
              <p className="label-mono text-[#71717A] mt-2">{order.shipping.phone}</p>
            </div>
          </div>
        </div>
      </div>
    </StoreLayout>
  );
}
